import { useCallback, useEffect, useState } from "react";
import { fetchCandidateTranscript } from "../utils/transcriptUtils";

export function useCandidateTranscript({
  enabled,
  applicant,
  setApplicant,
  companyId,
  jobId,
  token,
}) {
  const [isTranscriptLoading, setIsTranscriptLoading] = useState(false);
  const [transcriptError, setTranscriptError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);

  const resetTranscript = useCallback(() => {
    setIsTranscriptLoading(false);
    setTranscriptError(null);
    setRetryCount(0);
  }, []);

  const retryTranscript = useCallback(() => {
    setTranscriptError(null);
    setRetryCount((count) => count + 1);
  }, []);

  useEffect(() => {
    if (!enabled) {
      return undefined;
    }

    const candidateId = applicant?.id;
    const sessionId =
      applicant?.interview_session_id || applicant?.interview_session?.id;

    if (!candidateId || !companyId || !jobId || !token) {
      setIsTranscriptLoading(false);
      setTranscriptError(candidateId ? "Failed to load transcript." : null);
      return undefined;
    }

    const abort = new AbortController();
    let cancelled = false;
    const hasExistingTranscript =
      (applicant?.interview_responses || []).length > 0;

    if (!hasExistingTranscript) {
      setIsTranscriptLoading(true);
    }
    setTranscriptError(null);

    fetchCandidateTranscript(
      companyId,
      jobId,
      candidateId,
      token,
      abort.signal,
      sessionId
    )
      .then((responses) => {
        if (cancelled) {
          return;
        }
        setApplicant((prev) => {
          if (!prev || prev.id !== candidateId) {
            return prev;
          }
          if (
            sessionId &&
            prev.interview_session_id &&
            prev.interview_session_id !== sessionId
          ) {
            return prev;
          }
          return { ...prev, interview_responses: responses };
        });
      })
      .catch((error) => {
        if (cancelled || error?.name === "AbortError") {
          return;
        }
        if (!hasExistingTranscript) {
          setTranscriptError("Failed to load transcript.");
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsTranscriptLoading(false);
        }
      });

    return () => {
      cancelled = true;
      abort.abort();
    };
  }, [
    enabled,
    applicant?.id,
    applicant?.interview_session_id,
    applicant?.interview_session?.id,
    applicant?.analysis_status,
    applicant?.updated_at,
    retryCount,
    companyId,
    jobId,
    token,
    setApplicant,
  ]);

  return {
    isTranscriptLoading,
    transcriptError,
    retryTranscript,
    resetTranscript,
  };
}
